<template>
  <v-container fluid class="goals-page">
    <v-overlay absolute :value="isLoadingAny" opacity="0.12">
      <v-progress-circular indeterminate color="primary" size="48" />
    </v-overlay>

    <v-alert v-if="errorMessage && !hasMissingBaselineState" type="error" outlined dense class="mt-3">
      {{ errorMessage }}
    </v-alert>

    <v-row v-if="hasMissingBaselineState" dense class="mt-3">
      <v-col cols="12">
        <v-card class="baseline-setup-state" flat>
          <v-card-text class="baseline-setup-state__content">
            <div class="baseline-setup-state__icon">
              <v-icon color="primary" size="30">mdi-target-variant</v-icon>
            </div>
            <div class="baseline-setup-state__body">
              <strong>{{ missingBaselineTitle }}</strong>
              <span>{{ missingBaselineDescription }}</span>
            </div>
            <v-btn
              v-if="canConfigureBaseline"
              color="primary"
              depressed
              :to="baselineSetupRoute"
            >
              Configurar baseline
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="!hasMissingBaselineState" dense class="mt-3">
      <v-col cols="12" md="6">
        <v-card class="detail-card detail-card--compact goals-top-card" flat>
          <v-card-title>
            Período analisado
            <v-tooltip bottom max-width="320">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="#66788a"
                  class="help-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>{{ periodHelperText }}</span>
            </v-tooltip>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="dateFromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  content-class="goals-date-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="localDateFrom"
                      label="Mês inicial"
                      outlined
                      dense
                      hide-details
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="localDateFrom"
                    type="month"
                    no-title
                    scrollable
                    :min="baselineMinDate"
                    :max="dateFromMax"
                    class="goals-local-date-picker"
                    @input="dateFromMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="dateToMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  content-class="goals-date-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="localDateTo"
                      label="Mês final"
                      outlined
                      dense
                      hide-details
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="localDateTo"
                    type="month"
                    no-title
                    scrollable
                    :min="dateToMin"
                    :max="baselineMaxDate"
                    class="goals-local-date-picker"
                    @input="dateToMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="detail-card detail-card--compact header-reference-card goals-top-card" flat>
          <v-card-title>
            Referência em foco
            <v-tooltip bottom max-width="320">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="#66788a"
                  class="help-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>{{ baselineModeTooltip }}</span>
            </v-tooltip>
          </v-card-title>
          <v-card-text>
            <div class="header-reference-grid">
              <div>
                <span>Modo</span>
                <strong>{{ activeReferenceModeLabel }}</strong>
              </div>
              <div>
                <span>Vigência</span>
                <strong>
                  {{
                    currentReference
                      ? `${formatDisplayDate(currentReference.validFrom)}${currentReference.validTo ? ` - ${formatDisplayDate(currentReference.validTo)}` : " - aberto"}`
                      : "-"
                  }}
                </strong>
              </div>
              <div>
                <span>Meta de economia</span>
                <strong>{{ goalCalculation ? formatPercent(goalCalculation.metaEconomiaPercentual) : "-" }}</strong>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <template v-if="activeSummary.goalTotalNumeric !== null || activeSummary.consumptionTotalNumeric !== null">
      <v-row dense class="mt-3">
        <v-col cols="12" :md="goalCalculation && !isMonthlyBaselineReference ? 4 : (isMonthlyBaselineReference ? 8 : 12)">
          <div class="mode-switcher mode-switcher--secondary goals-top-card">
            <div class="mode-switcher__copy">
              <span>
                Recorte do gráfico
                <v-tooltip bottom max-width="260">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="#66788a"
                      class="help-icon"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>Total mostra água e efluentes juntos. Os outros recortes isolam cada componente do consumo e da meta.</span>
                </v-tooltip>
              </span>
              <p>{{ graphDescription }}</p>
            </div>
            <v-btn-toggle v-model="metricViewMode" mandatory dense class="mode-switcher__toggle">
              <v-btn
                v-for="mode in [{ text: 'Total', value: 'total' }, { text: 'Água', value: 'water' }, { text: 'Efluentes', value: 'sewer' }]"
                :key="mode.value"
                :value="mode.value"
                small
                depressed
              >
                {{ mode.text }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-col>

        <v-col v-if="goalCalculation && !isMonthlyBaselineReference" cols="12" md="8">
          <v-card class="detail-card detail-card--compact reference-calculation-card" flat>
            <v-card-title>
              Cálculo de meta
              <v-tooltip bottom max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#66788a"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>Mostra como a meta mensal da baseline fixa é formada: baseline de referência, ajuste do driver quando existir e desconto da meta de economia.</span>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <div class="reference-calculation-grid">
                <div class="reference-metric reference-metric--formula">
                  <span>
                    Meta água mensal
                    <v-tooltip bottom max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          x-small
                          color="#60758e"
                          class="help-icon"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ getReferenceFormula('water') }}</span>
                    </v-tooltip>
                  </span>
                  <strong>{{ formatVolume(goalCalculation.metaAgua) }}</strong>
                </div>
                <div class="reference-metric reference-metric--formula">
                  <span>
                    Meta de efluentes mensal
                    <v-tooltip bottom max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          x-small
                          color="#60758e"
                          class="help-icon"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ getReferenceFormula('sewer') }}</span>
                    </v-tooltip>
                  </span>
                  <strong>{{ formatVolume(goalCalculation.metaEsgoto) }}</strong>
                </div>
                <div class="reference-metric reference-metric--formula">
                  <span>
                    Meta total mensal
                    <v-tooltip bottom max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          x-small
                          color="#60758e"
                          class="help-icon"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ getReferenceFormula('total') }}</span>
                    </v-tooltip>
                  </span>
                  <strong>{{ formatVolume(goalCalculation.metaTotal) }}</strong>
                </div>
                <div class="reference-metric reference-metric--formula">
                  <span>
                    Economia aplicada
                    <v-tooltip bottom max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          x-small
                          color="#60758e"
                          class="help-icon"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ getReferenceFormula('economy') }}</span>
                    </v-tooltip>
                  </span>
                  <strong>{{ formatPercent(goalCalculation.metaEconomiaPercentual) }}</strong>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="isMonthlyBaselineReference && baselineTableRows.length > 0" cols="12" md="4">
          <v-card class="detail-card detail-card--compact goals-top-card table-navigation-card" flat>
            <v-card-title>Resumo mensal</v-card-title>
            <v-card-text>
              <v-btn
                block
                depressed
                color="primary"
                small
                @click="scrollToMonthlyTable"
              >
                Ver tabela mensal
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-3">
        <v-col cols="12" class="goal-chart-column goal-chart-column--no-export">
          <div class="chart-help">
            <v-tooltip bottom nudge-left="140" max-width="320" content-class="goals-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="#66788a"
                  class="help-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>{{ goalGraphTooltip }}</span>
            </v-tooltip>
          </div>
          <ConsumptionBarChart
            :title="`Consumo e meta (${volumeSuffix})`"
            :subtitle="''"
            :labels="combinedChartPayload.labels"
            :datasets="combinedChartPayload.dataset"
            :average="0"
            :loading="isLoadingAny"
            :show-goal-line="true"
          />
        </v-col>
      </v-row>

      <v-row v-if="!isMonthlyBaselineReference" dense class="mt-3">
        <v-col cols="12">
          <div class="goals-kpi-grid">
          <v-card class="metric-card metric-card--consumption metric-card--compact-label" flat>
            <span>Consumo real</span>
            <strong>{{ formatVolume(activeSummary.consumptionTotalNumeric) }}</strong>
          </v-card>
          <v-card class="metric-card metric-card--saving metric-card--compact-label" flat>
            <span class="metric-card__label">
              Economia água
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getSavingCardTooltip('water') }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolume(activeSummary.savingWaterNumeric) }}</strong>
          </v-card>
          <v-card class="metric-card metric-card--saving metric-card--compact-label" flat>
            <span class="metric-card__label">
              Economia efluentes
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getSavingCardTooltip('sewer') }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolume(activeSummary.savingSewerNumeric) }}</strong>
          </v-card>
          <v-card class="metric-card metric-card--saving metric-card--compact-label" flat>
            <span class="metric-card__label">
              Economia total
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getSavingCardTooltip('total') }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolume(activeSummary.savingTotalNumeric) }}</strong>
          </v-card>
          <v-card
            v-if="totalFinancialEconomyKpi"
            class="metric-card metric-card--financial-economy metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Economia financeira total
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getTotalFinancialEconomyTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatMoney(totalFinancialEconomyKpi.total) }}</strong>
          </v-card>
          <v-card
            v-if="baselineTableRows.length > 0"
            class="metric-card metric-card--total metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Meta de economia
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>Percentual de economia aplicado sobre a baseline para formar as metas de agua e efluentes.</span>
              </v-tooltip>
            </span>
            <strong>{{ formatPercent(activeSummary.metaEconomiaPercentual) }}</strong>
          </v-card>
          <v-card
            v-if="driverComparisonAverageKpi"
            class="metric-card metric-card--driver metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Média por driver
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getDriverComparisonAverageTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolumePerDriver(driverComparisonAverageKpi.historicalAverage, driverComparisonAverageKpi) }}</strong>
            <small class="metric-card__helper">
              Baseline média: {{ formatVolumePerDriver(driverComparisonAverageKpi.baselineAverage, driverComparisonAverageKpi) }}
            </small>
          </v-card>
          <v-card
            v-if="financialDriverComparisonAverageKpi"
            class="metric-card metric-card--driver-financial metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Média financeira por driver
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getFinancialDriverComparisonAverageTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatMoneyPerDriver(financialDriverComparisonAverageKpi.actualAverage, financialDriverComparisonAverageKpi) }}</strong>
            <small class="metric-card__helper">
              Baseline média: {{ formatMoneyPerDriver(financialDriverComparisonAverageKpi.baselineAverage, financialDriverComparisonAverageKpi) }}
            </small>
          </v-card>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="isMonthlyBaselineReference && activeSummary.savingTotalNumeric !== null" dense class="mt-3">
        <v-col cols="12">
          <div class="goals-kpi-grid">
          <v-card class="metric-card metric-card--saving metric-card--compact-label" flat>
            <span class="metric-card__label">
              Economia total
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getSavingCardTooltip('total') }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolume(activeSummary.savingTotalNumeric) }}</strong>
          </v-card>
          <v-card
            v-if="totalFinancialEconomyKpi"
            class="metric-card metric-card--financial-economy metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Economia financeira total
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getTotalFinancialEconomyTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatMoney(totalFinancialEconomyKpi.total) }}</strong>
          </v-card>
          <v-card
            v-if="baselineTableRows.length > 0"
            class="metric-card metric-card--total metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Meta de economia
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>Percentual de economia aplicado sobre a baseline para formar as metas de agua e efluentes.</span>
              </v-tooltip>
            </span>
            <strong>{{ formatPercent(activeSummary.metaEconomiaPercentual) }}</strong>
          </v-card>
          <v-card
            v-if="driverComparisonAverageKpi"
            class="metric-card metric-card--driver metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Média por driver
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getDriverComparisonAverageTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatVolumePerDriver(driverComparisonAverageKpi.historicalAverage, driverComparisonAverageKpi) }}</strong>
            <small class="metric-card__helper">
              Baseline média: {{ formatVolumePerDriver(driverComparisonAverageKpi.baselineAverage, driverComparisonAverageKpi) }}
            </small>
          </v-card>
          <v-card
            v-if="financialDriverComparisonAverageKpi"
            class="metric-card metric-card--driver-financial metric-card--compact-label"
            flat
          >
            <span class="metric-card__label">
              Média financeira por driver
              <v-tooltip bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#60758e"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ getFinancialDriverComparisonAverageTooltip() }}</span>
              </v-tooltip>
            </span>
            <strong>{{ formatMoneyPerDriver(financialDriverComparisonAverageKpi.actualAverage, financialDriverComparisonAverageKpi) }}</strong>
            <small class="metric-card__helper">
              Baseline média: {{ formatMoneyPerDriver(financialDriverComparisonAverageKpi.baselineAverage, financialDriverComparisonAverageKpi) }}
            </small>
          </v-card>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="baselineTableRows.length > 0" ref="monthlyBaselineTable" dense class="mt-3">
        <v-col cols="12">
          <v-card class="detail-card" flat>
            <v-card-title>
              Resumo mensal da baseline
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="#66788a"
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>Cada linha separa os dados cadastrados na baseline, os dados reais do histórico da unidade e os cálculos derivados, incluindo a comparação ajustada por driver.</span>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <div class="monthly-baseline-table">
                <table>
                  <thead>
                    <tr>
                      <th rowspan="2" class="monthly-baseline-table__month-header">
                        <span class="table-heading-help">
                          Mês
                          <v-tooltip bottom max-width="280">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="#60758e"
                                class="help-icon"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-help-circle-outline
                              </v-icon>
                            </template>
                            <span>{{ getMonthlyTableTooltip('month') }}</span>
                          </v-tooltip>
                        </span>
                      </th>
                      <th
                        v-for="group in monthlyBaselineTableGroups"
                        :key="group.key"
                        :colspan="group.columns.length"
                        :class="[
                          'monthly-baseline-table__group-header',
                          `monthly-baseline-table__group-header--${group.key}`,
                        ]"
                      >
                        <div class="monthly-baseline-table__group-header-content">
                          <strong>{{ group.label }}</strong>
                          <small>{{ group.description }}</small>
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <th
                        v-for="column in monthlyBaselineTableColumns"
                        :key="column.key"
                        :class="[
                          'monthly-baseline-table__column-header',
                          `monthly-baseline-table__column-header--${column.groupKey}`,
                          {
                            'monthly-baseline-table__column-start': column.isGroupStart,
                            'monthly-baseline-table__column-end': column.isGroupEnd,
                          },
                          { 'monthly-baseline-table__column-header--calculated': column.isCalculated },
                        ]"
                      >
                        <span class="table-heading-help">
                          {{ column.label }}
                          <v-tooltip bottom max-width="280">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="#60758e"
                                class="help-icon"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-help-circle-outline
                              </v-icon>
                            </template>
                            <span>{{ getMonthlyTableTooltip(column.key) }}</span>
                          </v-tooltip>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in baselineTableRows"
                      :key="row.monthKey"
                      :class="{ 'monthly-baseline-table__estimated-row': row.isEstimative }"
                    >
                      <td class="monthly-baseline-table__month-cell">
                        <span class="monthly-baseline-table__month">
                          {{ row.label }}
                          <v-tooltip v-if="row.isEstimative" bottom max-width="300">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="#2f7d73"
                                class="monthly-baseline-table__estimated-icon"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-chart-timeline-variant
                              </v-icon>
                            </template>
                            <span>Consumo estimado pela media dos registros reais anteriores.</span>
                          </v-tooltip>
                        </span>
                      </td>
                      <td
                        v-for="column in monthlyBaselineTableColumns"
                        :key="column.key"
                        :class="[
                          `monthly-baseline-table__cell--${column.groupKey}`,
                          {
                            'monthly-baseline-table__column-start': column.isGroupStart,
                            'monthly-baseline-table__column-end': column.isGroupEnd,
                          },
                          { 'monthly-baseline-table__cell--calculated': column.isCalculated },
                        ]"
                      >
                        <span
                          :class="{
                            'monthly-baseline-table__value-cell': column.isCalculated,
                          }"
                        >
                          <span>{{ formatBaselineTableValue(row, column.key) }}</span>
                          <v-tooltip
                            v-if="column.isCalculated && row.monthComparisons && row.monthComparisons[column.key]"
                            bottom
                            max-width="320"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                :color="row.monthComparisons[column.key].color"
                                class="monthly-baseline-table__comparison-icon"
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ row.monthComparisons[column.key].icon }}
                              </v-icon>
                            </template>
                            <span>{{ row.monthComparisons[column.key].tooltip }}</span>
                          </v-tooltip>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="baselineComparisonGroups.length > 1" dense class="mt-3">
        <v-col cols="12">
          <v-card class="detail-card" flat>
            <v-card-title>Comparação por baseline do período</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col
                  v-for="comparison in baselineComparisonGroups"
                  :key="comparison.configId"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <div class="baseline-comparison-card">
                    <span>Config {{ comparison.configId }}</span>
                    <strong>{{ comparison.mode === 'monthlyBaseline' ? 'Baseline mensal' : 'Baseline fixa' }}</strong>
                    <small>
                      {{
                        `${formatDisplayDate(comparison.validFrom)}${comparison.validTo ? ` - ${formatDisplayDate(comparison.validTo)}` : " - aberto"}`
                      }}
                    </small>
                    <small>Consumo real: {{ formatVolume(comparison.consumptionTotal) }}</small>
                    <small>Meta do período: {{ formatVolume(comparison.metaTotal) }}</small>
                    <small>Pontos na série: {{ comparison.pointsCount }}</small>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-row v-if="!errorMessage && !isLoadingAny && combinedChartPayload.labels.length === 0" class="mt-3">
      <v-col cols="12">
        <v-card class="empty-state" flat>
          <v-icon color="primary" size="36">mdi-target</v-icon>
          <strong>{{ emptyStateTitle }}</strong>
          <span>{{ emptyStateDescription }}</span>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import GoalsForscastsScript from "./GoalsForscastsScripts";
export default GoalsForscastsScript;
</script>

<style lang="scss" scoped>
.goals-page {
  min-height: 93vh;
  background: #fff;
  position: relative;
}

.goals-panel,
.detail-card,
.empty-state {
  border: 1px solid #e4eaf2;
  border-radius: 10px;
  background: #fff;
}

.goals-panel {
  padding: 20px;
}

.goals-panel__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    color: #1f3f67;
    font-size: 24px;
    line-height: 1.2;
  }

  p {
    margin: 6px 0 0;
    color: #66788a;
    font-size: 13px;
  }
}

.baseline-setup-state {
  border: 1px solid #d8e7ff;
  border-radius: 12px;
  background: linear-gradient(135deg, #f7fbff 0%, #ffffff 100%);
}

.baseline-setup-state__content {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px !important;
}

.baseline-setup-state__icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #eaf3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.baseline-setup-state__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #53616f;
}

.baseline-setup-state__body strong {
  color: #243447;
  font-size: 15px;
}

.baseline-setup-state__body span {
  font-size: 13px;
  line-height: 1.35;
}

@media (max-width: 760px) {
  .baseline-setup-state__content {
    align-items: flex-start;
    flex-direction: column;
  }
}

.selection-summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 42px;

  span {
    color: #66788a;
    font-size: 12px;
  }

  strong {
    color: #1f3f67;
    font-size: 15px;
    line-height: 1.2;
  }
}

.mode-switcher {
  border: 1px solid #dbe6f2;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.mode-switcher--secondary {
  height: 100%;
}

.mode-switcher__copy {
  min-width: 0;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1f3f67;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  p {
    margin: 4px 0 0;
    color: #66788a;
    font-size: 12px;
    line-height: 1.4;
  }
}

.goals-top-card {
  border-radius: 9px;
}

.goals-top-card.detail-card .v-card__title {
  font-size: 14px;
  padding: 9px 12px 0;
}

.goals-top-card.detail-card .v-card__text {
  padding: 6px 12px 10px;
}

.goals-top-card.mode-switcher {
  padding: 10px 12px;
  gap: 10px;
}

.goals-top-card .mode-switcher__copy span {
  font-size: 11px;
}

.goals-top-card .mode-switcher__copy p {
  font-size: 11px;
  line-height: 1.25;
  margin-top: 2px;
}

.goals-top-card::v-deep .v-input__slot {
  min-height: 34px !important;
}

.goals-top-card::v-deep .v-text-field__details {
  display: none;
}

.goals-top-card::v-deep .v-label {
  font-size: 12px;
}

.goals-top-card::v-deep input {
  font-size: 12px;
}

.goals-top-card::v-deep .v-btn {
  height: 28px !important;
  min-width: 64px !important;
  font-size: 11px;
}

.metric-card,
.comparison-card {
  min-height: 96px;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #dfe7f2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  span {
    color: #60758e;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
  }

  strong {
    color: #16385c;
    font-size: 24px;
    line-height: 1.1;
  }
}

.metric-card__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1.25;
}

.metric-card__helper {
  color: #5d7187;
  font-size: 11px;
  line-height: 1.35;
}

.metric-card--compact-label {
  padding: 14px;

  span {
    font-size: 11px;
  }

  strong {
    font-size: 22px;
  }
}

.metric-card--consumption {
  background: #eef5ff;
}

.metric-card--water {
  background: #eaf4ff;
}

.metric-card--sewer {
  background: #fff3e8;
}

.metric-card--saving {
  background: #f0fbf8;
}

.metric-card--total {
  background: #eef8f2;
}

.metric-card--driver {
  background: #f3f6ff;
}

.metric-card--driver-financial {
  background: #fff5eb;
}

.metric-card--financial-economy {
  background: #e9f8ef;
}

.goals-kpi-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;

  > .metric-card {
    flex: 1 1 0;
    min-width: 128px;
    min-height: 96px;
  }

  > .metric-card.metric-card--compact-label strong {
    font-size: clamp(15px, 1.15vw, 20px);
  }
}

.comparison-card--current {
  background: #edf7ff;
}

.comparison-card--historical {
  background: #f8f4ff;
}

.comparison-card--delta {
  background: #fff8e8;
}

.detail-card .v-card__title {
  color: #1f3f67;
  font-size: 16px;
  font-weight: 700;
  padding-bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.detail-card--compact .v-card__title {
  font-size: 15px;
  padding: 12px 16px 2px;
}

.detail-card--compact .v-card__text {
  padding: 6px 16px 14px;
}

.detail-card--compact .detail-grid {
  gap: 8px;

  div {
    padding-top: 8px;
  }
}

.header-reference-card {
  height: 100%;
}

.header-reference-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;

  div {
    border-top: 1px solid #edf1f6;
    padding-top: 5px;
    min-width: 0;
  }

  span {
    display: block;
    color: #66788a;
    font-size: 10px;
    margin-bottom: 2px;
    text-transform: uppercase;
    font-weight: 700;
  }

  strong {
    display: block;
    color: #263d57;
    font-size: 11px;
    line-height: 1.25;
    word-break: break-word;
  }
}

.help-icon {
  cursor: help;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  div {
    border-top: 1px solid #edf1f6;
    padding-top: 10px;
    min-width: 0;
  }

  span {
    display: block;
    color: #66788a;
    font-size: 12px;
    margin-bottom: 3px;
  }

  strong {
    display: block;
    color: #263d57;
    font-size: 14px;
    word-break: break-word;
  }
}

.detail-helper {
  display: block;
  color: #718399;
  font-size: 11px;
  line-height: 1.35;
  margin-top: 3px;
}

.detail-grid--single {
  grid-template-columns: 1fr;
}

.baseline-comparison-card {
  border: 1px solid #e3ebf4;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  background: #fbfdff;

  span {
    color: #6d8096;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
  }

  strong {
    color: #193b62;
    font-size: 16px;
    line-height: 1.2;
  }

  small {
    color: #5d7187;
    font-size: 12px;
    line-height: 1.4;
  }
}

.monthly-baseline-table {
  overflow-x: auto;
  border: 1px solid #e5ecf5;
  border-radius: 12px;

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1550px;
  }

  th,
  td {
    padding: 12px 10px;
    border-bottom: 1px solid #e7edf5;
    border-right: 1px solid #eef3f8;
    text-align: left;
    white-space: nowrap;
    font-size: 13px;
  }

  th {
    color: #60758e;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.03em;
    background: #f8fbff;
    vertical-align: middle;
  }

  td {
    color: #263d57;
  }

  tr > *:last-child {
    border-right: none;
  }

  .monthly-baseline-table__estimated-row {
    background: #f1faf8;

    td {
      border-bottom-color: #d5eee9;
    }
  }

  .monthly-baseline-table__estimated-row .monthly-baseline-table__cell--baseline {
    background: #f5fcfa;
  }

  .monthly-baseline-table__estimated-row .monthly-baseline-table__cell--historical {
    background: #f3fbf8;
  }

  .monthly-baseline-table__estimated-row .monthly-baseline-table__cell--calculated {
    background: #eef9f4;
  }
}

.monthly-baseline-table__month-header {
  background: #f8fbff !important;
  border-right: 2px solid #d6e1ee !important;
  position: sticky;
  left: 0;
  z-index: 5;
}

.monthly-baseline-table__group-header {
  text-align: center !important;
  font-size: 12px !important;
  padding: 10px 12px !important;
  border-bottom: 2px solid #dbe5ef !important;
}

.monthly-baseline-table__group-header-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.monthly-baseline-table__group-header-content strong {
  font-size: 12px;
  line-height: 1.2;
}

.monthly-baseline-table__group-header-content small {
  font-size: 10px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  opacity: 0.85;
}

.monthly-baseline-table__group-header--baseline {
  background: #edf4ff !important;
  color: #1f3f67 !important;
  border-left: 3px solid #c8daf5 !important;
  border-right: 3px solid #dfeaf9 !important;
}

.monthly-baseline-table__group-header--historical {
  background: #f6f8fb !important;
  color: #40566f !important;
  border-left: 3px solid #dde4ee !important;
  border-right: 3px solid #e7edf4 !important;
}

.monthly-baseline-table__group-header--calculated {
  background: #eef8f2 !important;
  color: #1f5b42 !important;
  border-left: 3px solid #cfe8d9 !important;
  border-right: 3px solid #dcefe3 !important;
}

.monthly-baseline-table__column-header--baseline {
  background: #f5f9ff !important;
}

.monthly-baseline-table__column-header--historical {
  background: #fbfcfe !important;
}

.monthly-baseline-table__column-header--calculated {
  background: #f4fbf6 !important;
}

.monthly-baseline-table__column-header--calculated,
.monthly-baseline-table__cell--calculated {
  color: #1f5b42;
  font-weight: 600;
}

.monthly-baseline-table__column-start {
  border-left-width: 3px !important;
  border-left-style: solid !important;
}

.monthly-baseline-table__column-end {
  border-right-width: 3px !important;
  border-right-style: solid !important;
}

.monthly-baseline-table__column-header--baseline.monthly-baseline-table__column-start,
.monthly-baseline-table__cell--baseline.monthly-baseline-table__column-start {
  border-left-color: #c8daf5 !important;
}

.monthly-baseline-table__column-header--baseline.monthly-baseline-table__column-end,
.monthly-baseline-table__cell--baseline.monthly-baseline-table__column-end {
  border-right-color: #dce8f8 !important;
}

.monthly-baseline-table__column-header--historical.monthly-baseline-table__column-start,
.monthly-baseline-table__cell--historical.monthly-baseline-table__column-start {
  border-left-color: #dde4ee !important;
}

.monthly-baseline-table__column-header--historical.monthly-baseline-table__column-end,
.monthly-baseline-table__cell--historical.monthly-baseline-table__column-end {
  border-right-color: #e7edf4 !important;
}

.monthly-baseline-table__column-header--calculated.monthly-baseline-table__column-start,
.monthly-baseline-table__cell--calculated.monthly-baseline-table__column-start {
  border-left-color: #cfe8d9 !important;
}

.monthly-baseline-table__column-header--calculated.monthly-baseline-table__column-end,
.monthly-baseline-table__cell--calculated.monthly-baseline-table__column-end {
  border-right-color: #dcefe3 !important;
}

.monthly-baseline-table__cell--baseline {
  background: #fcfdff;
}

.monthly-baseline-table__cell--historical {
  background: #ffffff;
}

.monthly-baseline-table__cell--calculated {
  background: #fbfefc;
}

.monthly-baseline-table__month-cell {
  background: #f8fbff;
  border-right: 2px solid #d6e1ee !important;
  font-weight: 700;
  position: sticky;
  left: 0;
  z-index: 4;
  box-shadow: 8px 0 12px -12px rgba(31, 63, 103, 0.35);
}

.monthly-baseline-table__month {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.monthly-baseline-table__value-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.monthly-baseline-table__comparison-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.monthly-baseline-table__estimated-icon {
  cursor: help;
}

.table-heading-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.reference-metric {
  min-height: 64px;
  border: 1px solid #e3ebf4;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fbfdff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;

  span {
    color: #60758e;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  strong {
    color: #16385c;
    font-size: 18px;
    line-height: 1.1;
  }
}

.reference-calculation-card {
  height: 100%;
}

.reference-calculation-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.reference-metric--formula {
  justify-content: flex-start;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.goal-chart-column {
  position: relative;
}

.goal-chart-column::v-deep .chart-responsive {
  height: 550px;
  min-height: 550px;
}

.goal-chart-column::v-deep .kpi-simple-card {
  padding: 10px 16px 8px;
}

.goal-chart-column::v-deep .kpi-header h2 {
  font-size: 1.15em !important;
  margin-bottom: 4px;
}

.goal-chart-column::v-deep .chart-container {
  height: 430px;
  min-height: 430px;
}

.goal-chart-column--no-export::v-deep .chart-type-select {
  display: none;
}

.chart-help {
  position: absolute;
  top: 13px;
  right: 54px;
  z-index: 3;
}

.table-navigation-card {
  height: 100%;
}

.goals-local-date-picker {
  min-height: 360px;
}

::v-deep .goals-local-date-picker .v-date-picker-table {
  height: auto;
  min-height: 242px;
}

::v-deep .goals-tooltip {
  max-width: 320px;
}

@media (max-width: 960px) {
  .chart-help {
    right: 46px;
  }
}

.empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #60758e;
  text-align: center;
  padding: 24px;

  strong {
    color: #1f3f67;
    font-size: 18px;
  }
}

@media (max-width: 960px) {
  .goals-panel__header,
  .mode-switcher {
    flex-direction: column;
    align-items: stretch;
  }

  .reference-calculation-grid,
  .header-reference-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

}
</style>

<style lang="scss">
.goals-date-menu {
  overflow: visible !important;
}

.goals-date-menu .v-date-picker-table {
  height: auto !important;
  min-height: 242px;
}
</style>
